import React, {
    createContext, useState, useEffect, useContext, useCallback, useRef,
} from 'react';
import { supabase } from '../services/supabase';
import { Session, User } from '@supabase/supabase-js';
import toast from 'react-hot-toast';
import {
    validateEmail, normalizeEmail, normalizePhone,
    validatePassword, validateRequired,
} from '../utils/validation';
import { validatePhoneNumber } from '../utils/phoneUtils';

export interface Customer {
    id: string;
    sequential_id?: number;
    phone_number: string;
    email: string;
    first_name: string;
    last_name: string;
    country_code: string;
    phone_country_code: string;
    is_active: boolean;
    created_at: string;
    last_login: string;
    email_confirmed: boolean;
    auth_user_id: string;
}

interface SignUpData {
    email: string; phone: string; password: string;
    firstName: string; lastName: string;
    phoneCode: string; countryCode: string;
}

interface AuthResult { success: boolean; error?: string; }

interface AuthContextType {
    user: Customer | null;
    session: Session | null;
    loading: boolean;
    pendingEmailChange: string | null;
    signUp: (data: SignUpData) => Promise<AuthResult>;
    signIn: (identifier: string, password: string) => Promise<AuthResult>;
    signOut: () => Promise<void>;
    updateProfile: (data: Partial<Customer>) => Promise<AuthResult>;
    resendConfirmationEmail: (email: string) => Promise<AuthResult>;
    sendPasswordReset: (email: string) => Promise<AuthResult>;
    initiateEmailChange: (newEmail: string) => Promise<AuthResult>;
    cancelEmailChange: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
};

function isResetPasswordPage(): boolean {
    return window.location.pathname === '/reset-password';
}

async function fetchCustomer(authUser: User): Promise<Customer | null> {
    const { data, error } = await supabase
        .from('customers').select('*')
        .eq('auth_user_id', authUser.id).maybeSingle();

    if (error) { console.error('[Auth] fetchCustomer error:', error.message); return null; }
    if (data) return { ...data, email_confirmed: authUser.email_confirmed_at !== null } as Customer;

    if (authUser.email) {
        const { data: byEmail, error: emailErr } = await supabase
            .from('customers').select('*').eq('email', authUser.email).maybeSingle();
        if (!emailErr && byEmail) {
            await supabase.from('customers')
                .update({ auth_user_id: authUser.id, updated_at: new Date().toISOString() })
                .eq('id', byEmail.id);
            return { ...byEmail, auth_user_id: authUser.id, email_confirmed: authUser.email_confirmed_at !== null } as Customer;
        }
    }

    const { data: created, error: insertErr } = await supabase
        .from('customers').insert({
            auth_user_id: authUser.id,
            email: authUser.email ?? '',
            phone_number: authUser.user_metadata?.phone_number ?? null,
            first_name: authUser.user_metadata?.first_name ?? '',
            last_name: authUser.user_metadata?.last_name ?? '',
            phone_country_code: authUser.user_metadata?.phone_country_code ?? '+992',
            country_code: authUser.user_metadata?.country_code ?? 'TJ',
            email_confirmed: authUser.email_confirmed_at !== null,
            is_active: true,
            last_login: new Date().toISOString(),
        }).select().single();

    if (insertErr) {
        console.error('[Auth] insert error:', insertErr.message);
        if (insertErr.code === '23505') {
            const { data: existing } = await supabase
                .from('customers').select('*').eq('email', authUser.email).maybeSingle();
            if (existing) return { ...existing, email_confirmed: authUser.email_confirmed_at !== null } as Customer;
        }
        return null;
    }

    return created as Customer;
}

function touchLastLogin(customerId: string) {
    supabase.from('customers')
        .update({ last_login: new Date().toISOString() })
        .eq('id', customerId)
        .then(({ error }) => { if (error) console.warn('[Auth] touchLastLogin:', error.message); });
}

function clearSupabaseStorage() {
    try {
        Object.keys(localStorage)
            .filter(k => k.match(/^sb-.+-auth-token$/) || k === 'sb-auth-token')
            .forEach(k => localStorage.removeItem(k));
        Object.keys(sessionStorage)
            .filter(k => k.match(/^sb-.+-auth-token$/) || k === 'sb-auth-token' || k === 'is_recovery_flow')
            .forEach(k => sessionStorage.removeItem(k));
        document.cookie.split(';').forEach(cookie => {
            const name = cookie.trim().split('=')[0];
            if (name?.match(/^sb-.+-auth-token$/)) {
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
            }
        });
    } catch (e) {
        console.warn('[Auth] clearSupabaseStorage error:', e);
    }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<Customer | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const [loading, setLoading] = useState(true);
    const [pendingEmailChange, setPendingEmailChange] = useState<string | null>(null);

    const mountedRef = useRef(true);
    const lastTokenRef = useRef<string | null>(null);
    const fetchingRef = useRef(false);

    // Вся логика в ref — useEffect с [] никогда не пересоздаёт подписку
    const handleSessionRef = useRef(async (newSession: Session | null) => {
        if (!mountedRef.current) return;

        setSession(newSession);

        if (!newSession?.user) {
            lastTokenRef.current = null;
            if (mountedRef.current) { setUser(null); setLoading(false); }
            return;
        }

        // Тот же токен — пользователь уже загружен
        if (lastTokenRef.current === newSession.access_token) {
            if (mountedRef.current) setLoading(false);
            return;
        }

        // Уже идёт загрузка — пропускаем
        if (fetchingRef.current) return;

        fetchingRef.current = true;
        lastTokenRef.current = newSession.access_token;

        try {
            const customer = await fetchCustomer(newSession.user);
            if (!mountedRef.current) return;
            if (customer) {
                setUser(customer);
                if (customer.email_confirmed) setPendingEmailChange(null);
            } else {
                setUser(null);
            }
        } catch (err) {
            console.error('[Auth] fetchCustomer threw:', err);
            if (mountedRef.current) setUser(null);
        } finally {
            fetchingRef.current = false;
            if (mountedRef.current) setLoading(false);
        }
    });

    useEffect(() => {
        mountedRef.current = true;

        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            (event, newSession) => {
                if (!mountedRef.current) return;
                console.debug('[Auth] event:', event);

                if (isResetPasswordPage()) {
                    if (event === 'PASSWORD_RECOVERY' || event === 'INITIAL_SESSION' || event === 'SIGNED_IN') {
                        setSession(newSession);
                        setLoading(false);
                    }
                    return;
                }

                switch (event) {
                    case 'INITIAL_SESSION':
                    case 'SIGNED_IN':
                    case 'TOKEN_REFRESHED':
                    case 'USER_UPDATED':
                        // void — не await внутри onAuthStateChange, иначе блокируется auth mutex
                        void handleSessionRef.current(newSession);
                        break;
                    case 'SIGNED_OUT':
                        lastTokenRef.current = null;
                        fetchingRef.current = false;
                        setUser(null);
                        setSession(null);
                        setLoading(false);
                        break;
                    case 'PASSWORD_RECOVERY':
                        setSession(newSession);
                        setUser(null);
                        setLoading(false);
                        break;
                    default:
                        break;
                }
            }
        );

        const safetyTimer = setTimeout(() => {
            if (mountedRef.current) {
                console.warn('[Auth] Safety timeout fired');
                setLoading(false);
            }
        }, 5000);

        return () => {
            mountedRef.current = false;
            subscription.unsubscribe();
            clearTimeout(safetyTimer);
        };
        // Пустой массив — подписка создаётся один раз и никогда не пересоздаётся
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const signUp = useCallback(async (data: SignUpData): Promise<AuthResult> => {
        if (!validateEmail(data.email)) return { success: false, error: 'Введите корректный email адрес' };
        const nameCheck = validateRequired(data.firstName, 'Имя');
        if (!nameCheck.isValid) return { success: false, error: nameCheck.error };
        const lastNameCheck = validateRequired(data.lastName, 'Фамилия');
        if (!lastNameCheck.isValid) return { success: false, error: lastNameCheck.error };
        const cleanPhone = normalizePhone(data.phone);
        if (!cleanPhone) return { success: false, error: 'Введите номер телефона' };
        if (!validatePhoneNumber(cleanPhone, data.phoneCode)) return { success: false, error: 'Неверный формат номера телефона' };
        const pwCheck = validatePassword(data.password);
        if (!pwCheck.isValid) return { success: false, error: pwCheck.error };
        const normalizedEmail = normalizeEmail(data.email);

        const [{ data: phoneExists }, { data: emailExists }] = await Promise.all([
            supabase.from('customers').select('id').eq('phone_number', cleanPhone).maybeSingle(),
            supabase.from('customers').select('id').eq('email', normalizedEmail).maybeSingle(),
        ]);
        if (phoneExists) return { success: false, error: 'Пользователь с таким номером телефона уже зарегистрирован' };
        if (emailExists) return { success: false, error: 'Пользователь с таким email уже зарегистрирован' };

        const { data: authData, error: signUpError } = await supabase.auth.signUp({
            email: normalizedEmail,
            password: data.password,
            options: {
                emailRedirectTo: `${window.location.origin}/auth/callback`,
                data: {
                    first_name: data.firstName.trim(),
                    last_name: data.lastName.trim(),
                    phone_number: cleanPhone,
                    phone_country_code: data.phoneCode,
                    country_code: data.countryCode,
                },
            },
        });

        if (signUpError) {
            if (signUpError.message.includes('already registered'))
                return { success: false, error: 'Пользователь с таким email уже зарегистрирован' };
            return { success: false, error: signUpError.message };
        }
        if (!authData.user) return { success: false, error: 'Не удалось создать пользователя' };

        await new Promise(resolve => setTimeout(resolve, 500));

        const { data: customer, error: customerError } = await supabase
            .from('customers').select('*').eq('auth_user_id', authData.user.id).maybeSingle();

        if (customerError || !customer) {
            await supabase.from('customers').insert({
                auth_user_id: authData.user.id,
                email: normalizedEmail,
                phone_number: cleanPhone,
                first_name: data.firstName.trim(),
                last_name: data.lastName.trim(),
                phone_country_code: data.phoneCode,
                country_code: data.countryCode,
                is_active: true,
                email_confirmed: false,
                last_login: new Date().toISOString(),
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
            }).select().single();
        }

        return { success: true };
    }, []);

    const signIn = useCallback(async (identifier: string, password: string): Promise<AuthResult> => {
        if (!identifier || !password) return { success: false, error: 'Заполните все поля' };
        let email = identifier.trim();

        if (/^[0-9+\s\-()]+$/.test(email)) {
            const cleanPhone = normalizePhone(email);
            const { data: customer, error } = await supabase
                .from('customers').select('email').eq('phone_number', cleanPhone).maybeSingle();
            if (error) console.warn('[Auth] phone lookup error:', error.message);
            if (!customer?.email) return { success: false, error: 'Пользователь с таким номером телефона не найден' };
            email = customer.email;
        }

        const { data, error } = await supabase.auth.signInWithPassword({
            email: normalizeEmail(email), password,
        });

        if (error) {
            if (error.message.includes('Invalid login credentials'))
                return { success: false, error: 'Неверный email/телефон или пароль' };
            if (error.message.includes('Email not confirmed'))
                return { success: false, error: 'Email не подтвержден. Пожалуйста, подтвердите email перед входом.' };
            return { success: false, error: error.message };
        }

        if (!data.user.email_confirmed_at) {
            await supabase.auth.resend({ type: 'signup', email: normalizeEmail(email) });
            await supabase.auth.signOut();
            return { success: false, error: 'Подтвердите email перед входом. Новое письмо отправлено.' };
        }

        const { data: activeCheck } = await supabase
            .from('customers').select('id, is_active, phone_number')
            .eq('auth_user_id', data.user.id).maybeSingle();

        if (activeCheck && !activeCheck.is_active) {
            await supabase.auth.signOut();
            return { success: false, error: 'Ваш аккаунт деактивирован. Обратитесь в поддержку.' };
        }

        if (activeCheck) {
            touchLastLogin(activeCheck.id);
            if (!activeCheck.phone_number) {
                const metaPhone = data.user.user_metadata?.phone_number;
                if (metaPhone) {
                    await supabase.from('customers').update({ phone_number: metaPhone }).eq('id', activeCheck.id);
                }
            }
        }

        return { success: true };
    }, []);

    const signOut = useCallback(async (): Promise<void> => {
        toast.loading('Выход из аккаунта...', { id: 'signout' });
        setUser(null);
        setSession(null);
        setPendingEmailChange(null);
        lastTokenRef.current = null;
        fetchingRef.current = false;
        try {
            await supabase.auth.signOut();
        } catch (err) {
            console.warn('[Auth] signOut error:', err);
        }
        clearSupabaseStorage();
        toast.success('Вы вышли из аккаунта', { id: 'signout' });
        window.location.replace('/login');
    }, []);

    const updateProfile = useCallback(async (data: Partial<Customer>): Promise<AuthResult> => {
        if (!user) return { success: false, error: 'Пользователь не авторизован' };
        const { id, sequential_id, auth_user_id, email_confirmed, created_at, last_login, email, ...updateData } = data as any;
        const { error } = await supabase.from('customers')
            .update({ ...updateData, updated_at: new Date().toISOString() }).eq('id', user.id);
        if (error) return { success: false, error: error.message };
        setUser(prev => prev ? { ...prev, ...updateData } : prev);
        return { success: true };
    }, [user]);

    const initiateEmailChange = useCallback(async (newEmail: string): Promise<AuthResult> => {
        if (!validateEmail(newEmail)) return { success: false, error: 'Введите корректный email адрес' };
        if (!user) return { success: false, error: 'Пользователь не авторизован' };
        const normalized = normalizeEmail(newEmail);
        if (normalized === normalizeEmail(user.email)) return { success: false, error: 'Новый email совпадает с текущим' };

        const { data: existing } = await supabase.from('customers')
            .select('id').eq('email', normalized).neq('id', user.id).maybeSingle();
        if (existing) return { success: false, error: 'Этот email уже используется' };

        const { error } = await supabase.auth.updateUser(
            { email: normalized },
            { emailRedirectTo: `${window.location.origin}/auth/callback` }
        );
        if (error) {
            if (error.message.includes('already registered') || error.message.includes('already been registered'))
                return { success: false, error: 'Этот email уже зарегистрирован' };
            return { success: false, error: error.message };
        }

        await supabase.from('customers').update({ email_confirmed: false }).eq('id', user.id);
        setUser(prev => prev ? { ...prev, email_confirmed: false } : prev);
        setPendingEmailChange(normalized);
        return { success: true };
    }, [user]);

    const resendConfirmationEmail = useCallback(async (email: string): Promise<AuthResult> => {
        if (!validateEmail(email)) return { success: false, error: 'Введите корректный email' };
        const { error } = await supabase.auth.resend({ type: 'signup', email: normalizeEmail(email) });
        if (error) {
            if (error.message.includes('already confirmed')) return { success: false, error: 'Email уже подтвержден' };
            return { success: false, error: error.message };
        }
        toast.success('Письмо отправлено повторно');
        return { success: true };
    }, []);

    const sendPasswordReset = useCallback(async (email: string): Promise<AuthResult> => {
        if (!validateEmail(email)) return { success: false, error: 'Введите корректный email' };
        const { error } = await supabase.auth.resetPasswordForEmail(normalizeEmail(email), {
            redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) return { success: false, error: error.message };
        toast.success('Инструкции по сбросу пароля отправлены на email');
        return { success: true };
    }, []);

    const cancelEmailChange = useCallback(() => setPendingEmailChange(null), []);

    return (
        <AuthContext.Provider value={{
            user, session, loading, pendingEmailChange,
            signUp, signIn, signOut, updateProfile,
            resendConfirmationEmail, sendPasswordReset,
            initiateEmailChange, cancelEmailChange,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export type { AuthContextType, AuthResult, SignUpData };